import React, { useState } from "react";
import { Button, InputNumber, Modal, message, Switch } from "antd";
import { ShieldAlert, Snowflake, Timer } from "lucide-react";
import { motion } from "framer-motion";
import blockchainApi from "../../api/blockchainApi";

/**
 * Админские действия над контрактом (роль operator).
 * Каждая кнопка отправляет ON-CHAIN транзакцию и тратит газ operator-кошелька,
 * поэтому всё под подтверждением.
 */

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const DAY = 86400;

export default function AdminControls({ paused }) {
    const [busy, setBusy] = useState(null);
    const [promoId, setPromoId] = useState(null);
    const [freeze, setFreeze] = useState(true);
    const [graceDays, setGraceDays] = useState(30);

    // Общий раннер: подтверждение → запрос → сообщение с txHash
    const run = (key, { title, content, okText, danger }, fn) => {
        Modal.confirm({
            title,
            content,
            okText,
            cancelText: "Отмена",
            okButtonProps: { danger },
            centered: true,
            onOk: async () => {
                setBusy(key);
                try {
                    const res = await fn();
                    const tx = res?.data?.txHash;
                    message.success(tx ? `Готово. Tx: ${tx.slice(0, 10)}…` : "Готово");
                } catch (e) {
                    const msg =
                        e?.response?.data?.message || e?.message || "Не удалось выполнить";
                    message.error(String(msg));
                } finally {
                    setBusy(null);
                }
            },
        });
    };

    const handlePause = () =>
        run(
            "pause",
            {
                title: "Остановить всю платформу?",
                content:
                    "Депозиты и клеймы встанут для ВСЕХ промо. Возврат остатков промоутерам продолжит работать. Используй при утечке ключа или найденном баге.",
                okText: "Да, остановить",
                danger: true,
            },
            () => blockchainApi.adminPause(),
        );

    const handleUnpause = () =>
        run(
            "unpause",
            {
                title: "Снять аварийный стоп?",
                content: "Депозиты и клеймы снова заработают для всех промо.",
                okText: "Снять стоп",
            },
            () => blockchainApi.adminUnpause(),
        );

    const handlePromoActive = () => {
        if (!promoId) {
            message.warning("Укажи ID промо");
            return;
        }
        run(
            "promo",
            {
                title: freeze ? `Заморозить промо #${promoId}?` : `Разморозить промо #${promoId}?`,
                content: freeze
                    ? "Клеймы по этому промо остановятся, и промоутер НЕ сможет забрать остаток — деньги будут ждать твоего решения."
                    : "Промо снова заработает: победители смогут клеймить, промоутер — забрать остаток после grace-периода.",
                okText: freeze ? "Заморозить" : "Разморозить",
                danger: freeze,
            },
            () => blockchainApi.adminSetPromoActive(Number(promoId), !freeze),
        );
    };

    const handleGrace = () =>
        run(
            "grace",
            {
                title: `Изменить окно клейма на ${graceDays} дн.?`,
                content:
                    "Столько времени после окончания промо победители смогут забирать призы. Раньше этого срока промоутер не заберёт остаток. Применяется ко всем промо.",
                okText: "Изменить",
            },
            () => blockchainApi.adminSetGracePeriod(Number(graceDays) * DAY),
        );

    return (
        <section className={cn(shellClass, "p-4 sm:p-5")}>
            <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-rose-500/10 blur-3xl" />
            <div className="relative">
                <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-rose-400/30 bg-rose-500/12">
                        <ShieldAlert className="h-5 w-5 text-rose-300/90" strokeWidth={2} />
                    </span>
                    <div>
                        <h2 className="text-base font-semibold tracking-tight text-white/95">
                            Управление контрактом
                        </h2>
                        <p className="text-[12px] text-white/45">
                            Действия отправляются в сеть и тратят газ. Доступно только админам.
                        </p>
                    </div>
                    {paused && (
                        <span className="ml-auto rounded-lg border border-rose-400/35 bg-rose-500/12 px-2.5 py-1 text-[12px] text-rose-100">
                            Платформа остановлена
                        </span>
                    )}
                </div>

                <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                    {/* Аварийный стоп */}
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                        <div className="mb-2 flex items-center gap-2 text-[13px] font-medium text-white/85">
                            <ShieldAlert className="h-4 w-4 text-rose-300/80" />
                            Аварийный стоп
                        </div>
                        <p className="mb-3 text-[12px] leading-relaxed text-white/45">
                            Останавливает депозиты и клеймы на всей платформе.
                        </p>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            {paused ? (
                                <Button
                                    block
                                    loading={busy === "unpause"}
                                    onClick={handleUnpause}
                                    className="!rounded-xl !border-emerald-400/30 !bg-emerald-500/10 !text-emerald-300 hover:!border-emerald-400/50 hover:!bg-emerald-500/20"
                                >
                                    Снять стоп
                                </Button>
                            ) : (
                                <Button
                                    block
                                    danger
                                    loading={busy === "pause"}
                                    onClick={handlePause}
                                    className="!rounded-xl !border-rose-400/30 !bg-rose-500/10 !text-rose-300 hover:!border-rose-400/50 hover:!bg-rose-500/20"
                                >
                                    Остановить платформу
                                </Button>
                            )}
                        </motion.div>
                    </div>

                    {/* Заморозка промо */}
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                        <div className="mb-2 flex items-center gap-2 text-[13px] font-medium text-white/85">
                            <Snowflake className="h-4 w-4 text-sky-300/80" />
                            Заморозка промо
                        </div>
                        <p className="mb-3 text-[12px] leading-relaxed text-white/45">
                            Точечно: клеймы стоят, промоутер не заберёт остаток.
                        </p>
                        <div className="mb-2.5 flex items-center gap-2">
                            <InputNumber
                                min={1}
                                placeholder="ID промо"
                                value={promoId}
                                onChange={setPromoId}
                                className="!w-full"
                            />
                            <div className="flex shrink-0 items-center gap-1.5">
                                <Switch size="small" checked={freeze} onChange={setFreeze} />
                                <span className="text-[12px] text-white/55">
                                    {freeze ? "заморозить" : "разморозить"}
                                </span>
                            </div>
                        </div>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                block
                                loading={busy === "promo"}
                                onClick={handlePromoActive}
                                className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10"
                            >
                                Применить
                            </Button>
                        </motion.div>
                    </div>

                    {/* Окно клейма */}
                    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3.5">
                        <div className="mb-2 flex items-center gap-2 text-[13px] font-medium text-white/85">
                            <Timer className="h-4 w-4 text-violet-300/80" />
                            Окно клейма
                        </div>
                        <p className="mb-3 text-[12px] leading-relaxed text-white/45">
                            Сколько дней после конца промо победители могут забрать приз.
                        </p>
                        <InputNumber
                            min={1}
                            max={365}
                            value={graceDays}
                            onChange={setGraceDays}
                            addonAfter="дн."
                            className="mb-2.5 !w-full"
                        />
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                            <Button
                                block
                                loading={busy === "grace"}
                                onClick={handleGrace}
                                className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10"
                            >
                                Изменить
                            </Button>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
